import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import {
  StructuredSearchResult,
  StructuredSearchAggregations,
} from '@core/Data/StructuredSearch/StructuredSearchResult'
import { RangeBoundaries } from '../RangeBoundaries'
import { filterSelectedTracksTrackIds } from '../Components/StructuredSearchLocation/track-utils'

export type SelectedTrackType = string[] | null

export interface StructuredSearchFields {
  contestTypeRace: boolean
  contestTypeTrot: boolean
  contestTypeDogs: boolean
  dateToday: boolean
  dateTomorrow: boolean
  dateBeyondTomorrow: boolean
  wonLastStart: boolean
  placedLastStart: boolean
  gearChange: boolean
  firstUp: boolean
  secondUp: boolean
  careerPrizeMoney: string
  careerWinPercentage: number
  trackWinPercentage: number
  distanceWinPercentage: number
  trackdistanceWinPercentage: number
  barrierNumberOneToFive: boolean
  barrierNumberSixPlus: boolean
  boxNumberOneToFour: boolean
  boxNumberFivePlus: boolean
  apprentice: boolean
  favouriteLastStart: boolean
  weightChangedUp: boolean
  weightChangedDown: boolean
  weightNotChanged: boolean
  distanceChangedUp: boolean
  distanceChangedDown: boolean
  distanceNotChanged: boolean
  minFixedOddsPrice: number
  maxFixedOddsPrice: number
  minDaysSinceLastStart: number
  maxDaysSinceLastStart: number
  minDaysSinceLastWin: number
  maxDaysSinceLastWin: number
  minStartsSinceLastSpell: number
  maxStartsSinceLastSpell: number
  winTrackConditionFirm: boolean
  winTrackConditionGood: boolean
  winTrackConditionHeavy: boolean
  winTrackConditionSoft: boolean
  winTrackConditionSynthetic: boolean
  locationCountryAustralia: boolean
  locationCountryNewZealand: boolean
  locationCountryInternational: boolean
  selectedTracks: SelectedTrackType
}

export interface IStructuredSearchResults {
  results: StructuredSearchResult[]
  totalResultCount: number
  aggregations: StructuredSearchAggregations | null
}

export interface StructuredSearchState {
  searchCriteria: StructuredSearchFields
  searchResults: IStructuredSearchResults
}

const initialSearchCriteria: StructuredSearchFields = {
  contestTypeRace: false,
  contestTypeTrot: false,
  contestTypeDogs: false,
  dateToday: false,
  dateTomorrow: false,
  dateBeyondTomorrow: false,
  wonLastStart: false,
  placedLastStart: false,
  gearChange: false,
  firstUp: false,
  secondUp: false,
  careerPrizeMoney: '',
  careerWinPercentage: RangeBoundaries.minPercentage,
  trackWinPercentage: RangeBoundaries.minPercentage,
  distanceWinPercentage: RangeBoundaries.minPercentage,
  trackdistanceWinPercentage: RangeBoundaries.minPercentage,
  barrierNumberOneToFive: false,
  barrierNumberSixPlus: false,
  boxNumberOneToFour: false,
  boxNumberFivePlus: false,
  apprentice: false,
  favouriteLastStart: false,
  weightChangedUp: false,
  weightChangedDown: false,
  weightNotChanged: false,
  distanceChangedUp: false,
  distanceChangedDown: false,
  distanceNotChanged: false,
  winTrackConditionFirm: false,
  winTrackConditionGood: false,
  winTrackConditionHeavy: false,
  winTrackConditionSoft: false,
  winTrackConditionSynthetic: false,
  locationCountryAustralia: false,
  locationCountryNewZealand: false,
  locationCountryInternational: false,
  selectedTracks: null,
  ...RangeBoundaries,
}

const initialSearchResults: IStructuredSearchResults = {
  results: [],
  totalResultCount: 0,
  aggregations: null,
}

export const initialState: StructuredSearchState = {
  searchCriteria: initialSearchCriteria,
  searchResults: initialSearchResults,
}

const structuredSearchSlice = createSlice({
  name: 'structuredSearch',
  initialState,
  reducers: {
    setSearchField<K extends keyof StructuredSearchFields>(
      state: StructuredSearchState,
      action: PayloadAction<{ field: K; value: StructuredSearchFields[K] }>
    ) {
      state.searchCriteria[action.payload.field] = action.payload.value
    },
    setSearchCriteria(state, action: PayloadAction<StructuredSearchFields>) {
      state.searchCriteria = action.payload
    },
    setSelectedTracks(state, action: PayloadAction<SelectedTrackType>) {
      state.searchCriteria.selectedTracks = action.payload
    },
    setSearchResults(state, action: PayloadAction<IStructuredSearchResults>) {
      const { results, totalResultCount, aggregations } = action.payload
      state.searchResults.results = results
      state.searchResults.totalResultCount = totalResultCount
      state.searchResults.aggregations = aggregations

      if (aggregations?.tracks) {
        const filtered = filterSelectedTracksTrackIds(
          aggregations.tracks,
          state.searchCriteria.selectedTracks
        )
        state.searchCriteria.selectedTracks = filtered
      }
    },
  },
})

export const { setSearchField, setSearchCriteria, setSelectedTracks, setSearchResults } =
  structuredSearchSlice.actions

export default persistReducer(
  {
    key: 'structuredSearch',
    whitelist: ['searchCriteria'],
    storage,
    // debug: !PRODUCTION,
  },
  structuredSearchSlice.reducer
)
